import React from 'react';
import { evaluationPercentage, percentageLabel } from '../lib/evaluationIntegrity';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';

const COLORS = {
  critico: '#DC2626', alto: '#D97706', medio: '#CA8A04', bajo: '#15803D',
  primary: '#1A56DB', secondary: '#0D1B2A',
  eventos: '#1A56DB', security: '#7C3AED', safety: '#15803D',
};

// ─── Radar: sección puntaje actual vs máximo ────────────────────────────────
export function SectionRadarChart({ sectionStats, size = 280 }) {
  // Antes los tres ejes estaban fijos en EVENTOS / SECURITY / SAFETY. Una
  // plantilla con otros nombres de sección dejaba los tres lookups en
  // undefined y el radar se dibujaba colapsado en el centro (0%) al lado
  // del puntaje real, como si la evaluación estuviera vacía. Ahora los ejes
  // salen de las secciones que realmente tiene la evaluación.
  const data = Object.entries(sectionStats || {}).map(([section, s]) => ({
    section: s.title || section,
    pct: s?.percentage ?? null,
  }));
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={size}>
      <RadarChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="section" tick={{ fill: '#0D1B2A', fontSize: 12, fontWeight: 700 }} />
        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
        <Radar name="Puntaje" dataKey="pct" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.25} strokeWidth={2} />
        <Tooltip formatter={(v) => [percentageLabel(v), 'Puntaje']} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ─── Bar: puntaje por sección ───────────────────────────────────────────────
export function SectionBarChart({ sectionStats }) {
  const data = Object.entries(sectionStats).map(([sec, s]) => ({
    name: s.title || sec,
    Obtenido: s.score,
    Máximo: s.max,
    pct: s.percentage ?? null,
  }));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 700, fill: '#0D1B2A' }} />
        <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
        <Tooltip formatter={(v, name) => [v, name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="Obtenido" fill={COLORS.primary} radius={[3, 3, 0, 0]} />
        <Bar dataKey="Máximo" fill="#e2e8f0" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Line: evolución de puntaje por versión ─────────────────────────────────
export function VersionTrendChart({ evaluations }) {
  const data = [...evaluations]
    .filter(e => e.status === 'publicada')
    .sort((a, b) => a.version - b.version)
    .map(e => ({
      version: `v${e.version}`,
      Puntaje: evaluationPercentage(e),
      Instalación: e.general_fields?.instalacion || `v${e.version}`,
    }));

  if (data.length < 2) return (
    <div className="flex items-center justify-center h-40 text-slate-400 text-sm">
      Se necesitan al menos 2 versiones publicadas para mostrar la tendencia.
    </div>
  );

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="version" tick={{ fontSize: 11, fill: '#0D1B2A' }} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} unit="%" />
        <Tooltip formatter={(v) => [percentageLabel(v), 'Puntaje']} />
        <Line type="monotone" dataKey="Puntaje" stroke={COLORS.primary} strokeWidth={2.5} dot={{ fill: COLORS.primary, r: 5 }} activeDot={{ r: 7 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Bar: distribución por semáforo ─────────────────────────────────────────
export function SemaforoDistChart({ evaluations }) {
  const counts = { critico: 0, alto: 0, medio: 0, bajo: 0 };
  evaluations.forEach(e => { if (counts[e.semaforo] !== undefined) counts[e.semaforo]++; });
  const data = [
    { name: 'CRÍTICO', value: counts.critico, color: COLORS.critico },
    { name: 'ALTO', value: counts.alto, color: COLORS.alto },
    { name: 'MEDIO', value: counts.medio, color: COLORS.medio },
    { name: 'BAJO', value: counts.bajo, color: COLORS.bajo },
  ].filter(d => d.value > 0);

  if (data.length === 0) return <div className="flex items-center justify-center h-32 text-slate-400 text-sm">Sin datos.</div>;

  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="name" tick={{ fontSize: 10, fontWeight: 700 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
        <Tooltip />
        <Bar dataKey="value" name="Evaluaciones" radius={[3, 3, 0, 0]}>
          {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Bar: distribución por estado ───────────────────────────────────────────
export function StatusDistChart({ evaluations }) {
  const STATUS_COLORS = {
    borrador: '#94a3b8', en_revision: '#1A56DB',
    regresada: '#D97706', publicada: '#15803D', archivada: '#475569',
  };
  const STATUS_LABELS = {
    borrador: 'Borrador', en_revision: 'En Revisión',
    regresada: 'Regresada', publicada: 'Publicada', archivada: 'Archivada',
  };
  const counts = {};
  evaluations.forEach(e => { counts[e.status] = (counts[e.status] || 0) + 1; });
  const data = Object.entries(counts).map(([status, value]) => ({
    name: STATUS_LABELS[status] || status,
    value,
    color: STATUS_COLORS[status] || '#94a3b8',
  }));

  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="name" tick={{ fontSize: 10, fontWeight: 700 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#94a3b8' }} />
        <Tooltip />
        <Bar dataKey="value" name="Evaluaciones" radius={[3, 3, 0, 0]}>
          {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
